import React, { useRef, useState, memo } from "react";

const CheckboxControlled = ({ handleChange, value, defaultChecked, label }) => (
  <label className="flex cursor-pointer">
    <input
      tabIndex={0}
      type="checkbox"
      checked={defaultChecked}
      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mr-3 shrink-0 self-center"
      onChange={() => handleChange(value, !defaultChecked)}
    />{" "}
    {label}
  </label>
);

export const Checkbox = ({
  handleChange,
  value,
  defaultChecked,
  label,
  height = "h-4",
  width = "w-4"
}) => {
  const checkboxRef = useRef(null);

  return (
    <label className="flex cursor-pointer">
      <input
        tabIndex={0}
        ref={checkboxRef}
        type="checkbox"
        defaultChecked={defaultChecked}
        className={`focus:ring-indigo-500 ${height} ${width} text-indigo-600 border-gray-300 rounded mr-3 shrink-0 self-center`}
        onChange={() => {
          handleChange(value, checkboxRef.current.checked);
        }}
      />{" "}
      {label}
    </label>
  );
};

const CheckboxList = memo(
  ({
    input: { onChange, value: values = [] },
    options = [],
    paddingX = "px-2",
    className = "",
    groupBy
  }) => {
    const [state, setState] = useState(new Set(values));
    let lastHeading = null;
    let currentHeading = null;

    const handleChange = (value, checked) => {
      if (checked) {
        setState(state.add(value));
      }
      if (!checked) {
        state.delete(value);
        setState(state);
      }
      onChange([...state]);
    };

    return (
      <ul className={`${paddingX} text-base ${className}`}>
        {options.map(({ value, label, ...rest }) => {
          let groupHeading = null;
          if (groupBy) {
            currentHeading = rest[groupBy];
            if (lastHeading !== currentHeading) {
              groupHeading = (
                <h2 className="font-semibold text-base mt-2 mb-.5">
                  {currentHeading}
                </h2>
              );
              lastHeading = currentHeading;
            }
          }

          return (
            <li
              className="border-b border-gray-200 last:border-b-0 py-1"
              key={value}
            >
              {groupHeading}
              <Checkbox
                handleChange={handleChange}
                value={value}
                defaultChecked={state.has(value)}
                label={label}
              />
            </li>
          );
        })}
      </ul>
    );
  }
);

const doAlphabetize = (options, alphabetize) => {
  if (!alphabetize) return options;
  return [...options].sort(({ label: a }, { label: b }) =>
    a < b ? -1 : a > b ? 1 : 0
  );
};

export const CheckboxListExternalState = ({
  input: { onChange },
  options = [],
  paddingX = "px-2",
  className = "",
  state,
  alphabetize = false,
  textSize = "text-base",
  groupBy
}) => {
  let lastHeading = null;
  let currentHeading = null;

  const handleChange = async (value, checked) => {
    await onChange(value, checked);
  };

  return (
    <ul className={`${paddingX} text-base ${className}`}>
      {doAlphabetize(options, alphabetize).map(
        ({ value, label, highlighted = false, ...rest }) => {
          let groupHeading = null;

          if (groupBy) {
            currentHeading = rest[groupBy];
            if (lastHeading !== currentHeading) {
              groupHeading = (
                <h2 className="font-semibold text-base mt-2 mb-.5">
                  {currentHeading}
                </h2>
              );
              lastHeading = currentHeading;
            }
          }

          return (
            <li
              className={`border-b border-gray-200 last:border-b-0 py-1 px-1 ${textSize} ${
                highlighted ? "bg-indigo-50" : ""
              }`}
              key={value}
            >
              {groupHeading}
              <CheckboxControlled
                handleChange={handleChange}
                value={value}
                defaultChecked={state.has(value)}
                label={label}
              />
            </li>
          );
        }
      )}
    </ul>
  );
};

export default CheckboxList;
